import { ArrowUp, Mail, MapPin } from "lucide-react";
import { useLenis } from "@/components/layout/SmoothScrollProvider";
import { Link } from "react-router-dom";

export default function Footer() {
  const lenisRef = useLenis();

  const scrollToTop = () => {
    if (lenisRef?.current) {
      lenisRef.current.scrollTo(0, { duration: 1.5 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const navLinks = [
    { label: "Expertises", href: "/#expertises" },
    { label: "Qui sommes-nous ?", href: "/#qui-sommes-nous" },
    { label: "Secteurs", href: "/#secteurs" },
    { label: "Carrières", href: "/carrieres" },
    { label: "Contact", href: "/#contact" },
  ];

  const legalLinks = [
    { label: "Mentions légales", href: "/mentions-legales" },
    { label: "Politique de confidentialité", href: "/confidentialite" },
  ];

  return (
    <footer className="relative bg-[#03050A] pt-24 pb-12 overflow-hidden">
      {/* Ultra-fine top separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-white/[0.05]" />

      <div className="mx-auto max-w-7xl px-6 md:px-8 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-16 lg:gap-8">
          
          {/* Column 1: Brand & Tagline - 5/12 */}
          <div className="lg:col-span-5 flex flex-col items-start">
            <Link to="/" onClick={scrollToTop} className="inline-block group transition-opacity hover:opacity-80">
              <img
                src="/lenvia-logo.png"
                alt="Lenvia"
                className="h-9 w-auto"
              />
            </Link>

            <p className="mt-8 text-[15px] leading-relaxed text-slate-400 max-w-xs">
              Partenaire technique des DSI et CTO. 
              Systèmes critiques et ingénierie de précision.
            </p>

            <div className="mt-10 flex flex-col gap-5">
              <a
                href="mailto:contact@lenvia.eu"
                className="group flex items-center gap-3.5 text-[14px] text-slate-400 hover:text-white transition-colors duration-300"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.06] bg-white/[0.02] group-hover:border-white/20 group-hover:bg-white/[0.05] transition-all duration-300">
                  <Mail className="h-3.5 w-3.5 text-slate-500 group-hover:text-lenvia-blue" />
                </div>
                <span>contact@lenvia.eu</span>
              </a>

              <div className="flex items-center gap-3.5 text-[14px] text-slate-400">
                <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.06] bg-white/[0.02]">
                  <MapPin className="h-3.5 w-3.5 text-slate-500" />
                </div>
                <span>Paris, France</span>
              </div>
            </div>
          </div>

          {/* Column 2: Navigation - 3/12 */}
          <div className="lg:col-span-3">
            <h4 className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-8">
              Navigation
            </h4>
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-[14px] text-slate-400 hover:text-white transition-colors duration-200"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Column 3: Légal & Groupe - 4/12 */}
          <div className="lg:col-span-4 flex flex-col justify-between">
            <div>
              <h4 className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-8">
                Légal
              </h4>
              <nav className="flex flex-col gap-4">
                {legalLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className="text-[14px] text-slate-400 hover:text-white transition-colors duration-200 text-balance"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="mt-12 pt-8 border-t border-white/[0.05]">
              <p className="text-[13px] leading-relaxed text-slate-500 max-w-sm">
                Lenvia s’appuie sur la solidité et l’envergure du groupe 
                <a href="https://proxiad.com" target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white transition-colors mx-1">Proxiad</a>, 
                acteur de référence des services numériques.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Scroll */}
        <div className="mt-20 pt-8 border-t border-white/[0.05]">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-8">
            <p className="text-[12px] font-medium tracking-wide text-slate-600">
              © {new Date().getFullYear()} LENVIA. TOUS DROITS RÉSERVÉS.
            </p>

            <button
              type="button"
              onClick={scrollToTop}
              className="group flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-all duration-300"
              aria-label="Retour en haut de page"
            >
              Haut de page
              <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/[0.08] group-hover:border-white/30 group-hover:bg-white/[0.05] transition-all duration-300">
                <ArrowUp className="h-3.5 w-3.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
